import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute, Link } from "@tanstack/react-router";

import { EnquiryForm } from "@/components/enquiry-form";
import { FaqList, PageHero, Prose } from "@/components/page-parts";
import { ReviewStrip } from "@/components/review-strip";
import {
  CONTACT_NUMBERS,
  FIRM,
  FIRM_ADDRESS,
  MICHAEL_PHONE,
  PEOPLE,
  faqSchema,
} from "@/lib/firm";

const SITE = "https://coastsidelaw.com.au";
const CANONICAL = `${SITE}/wills-and-probate-golden-beach`;
const CONVEYANCING = `${SITE}/conveyancing/golden-beach`;

const TITLE = "Wills, Estates & Probate Solicitors — Golden Beach QLD 4551 | Coastside Law";
const DESCRIPTION =
  "Coastside Law is a law firm at 40 Theresa Street, Golden Beach, preparing wills and enduring powers of attorney at fixed fees from $660 and acting for executors through probate and estate administration.";

/**
 * Entity page for the Golden Beach principal office. Unlike the service-area
 * pages, Golden Beach is a real staffed premises, so the schema states it as
 * the firm's own address and links the node to the site Organization and to
 * the Golden Beach conveyancing page, keeping one local entity cluster.
 */
const ANSWER =
  `${FIRM.name} is a Queensland law firm at ${FIRM_ADDRESS}, preparing wills, enduring powers of attorney and advance health directives for Golden Beach residents and acting for executors through probate and estate administration. ` +
  `In Queensland a will, an enduring power of attorney and a probate application are legal work: they are handled by an admitted solicitor, not a will-kit service or a licensed conveyancer, and probate is granted by the Supreme Court of Queensland. ` +
  `Simple wills start from $660, with fixed fees of $825 for a will and enduring power of attorney pack and $1,499 for a couples pack of mirror wills and enduring powers of attorney, all inclusive of GST; probate and estate administration are quoted as a fixed fee upfront with Supreme Court filing and advertising at cost. ` +
  `We see Golden Beach clients at the Theresa Street office and visit at home, in hospital or in aged care where getting to an office is not practical, store original wills in safe custody free of charge, and are members of the ${FIRM.membership.replace("Members of the ", "")}. ` +
  `Call ${FIRM.phone} (Erin Wilkinson) or ${MICHAEL_PHONE} (Michael Klein).`;

const VARIANTS: ReadonlyArray<string> = [
  "wills solicitor Golden Beach",
  "will lawyer Golden Beach Caloundra",
  "wills and estates Golden Beach",
  "how much does a will cost in Golden Beach",
  "enduring power of attorney Golden Beach",
  "probate solicitor Golden Beach",
  "probate lawyer Caloundra 4551",
  "deceased estate solicitor Golden Beach",
  "estate administration Golden Beach",
  "wills solicitor near me Golden Beach",
];

const FACTS: ReadonlyArray<{ label: string; value: string }> = [
  { label: "Entity", value: `${FIRM.legalName} — law firm, solicitor-led` },
  { label: "Office", value: `${FIRM_ADDRESS} — principal office, open to the public` },
  {
    label: "Area served",
    value: "Golden Beach QLD 4551, Pelican Waters, Caloundra, Kings Beach, Moffat Beach, Little Mountain, Currimundi",
  },
  {
    label: "Services",
    value:
      "Wills, enduring powers of attorney, advance health directives, probate, letters of administration, estate administration, estate disputes",
  },
  { label: "Phone", value: `${FIRM.phone} (Erin Wilkinson) · ${MICHAEL_PHONE} (Michael Klein)` },
  { label: "Email", value: FIRM.email },
  { label: "Professional body", value: FIRM.membership },
  { label: "Hours", value: FIRM.hours },
  { label: "Home, hospital and aged care visits", value: "Yes, across Golden Beach and the Caloundra area" },
];

const SERVICES: ReadonlyArray<{ name: string; price: string; body: string; to: string }> = [
  {
    name: "Simple will",
    price: "from $660 incl. GST",
    body: "A solicitor-drafted will covering the Golden Beach home and the usual bank, super and personal assets, signed and witnessed correctly, with free safe custody of the original.",
    to: "/wills-and-estates",
  },
  {
    name: "Will and enduring power of attorney pack",
    price: "$825 incl. GST",
    body: "A will plus an enduring power of attorney covering both financial and personal or health decisions — the pairing that matters most as people move into their seventies and eighties.",
    to: "/wills-and-estates",
  },
  {
    name: "Couples pack",
    price: "$1,499 incl. GST",
    body: "Mirror wills and enduring powers of attorney for a couple, prepared and signed in one appointment at the Theresa Street office.",
    to: "/wills-and-estates",
  },
  {
    name: "Probate and letters of administration",
    price: "Fixed fee, quoted upfront",
    body: "Advertising the intention to apply, preparing the application and affidavits, and filing in the Supreme Court of Queensland.",
    to: "/probate-queensland",
  },
  {
    name: "Estate administration",
    price: "Fixed fee, quoted upfront",
    body: "Collecting assets, transmitting or selling the Golden Beach property, dealing with super and shares, and distributing to beneficiaries.",
    to: "/executor-queensland",
  },
  {
    name: "Granny flat agreements and elder law",
    price: "Quoted on the facts",
    body: "Where a parent contributes to a build or moves in with adult children, a written agreement setting out contributions and occupation rights — with the Centrelink consequences explained.",
    to: "/elder-law",
  },
];

const LOCAL_NOTES: ReadonlyArray<{ t: string; d: string }> = [
  {
    t: "A retirement suburb, so estates come up often",
    d: "Golden Beach has a much older median age than the Sunshine Coast as a whole. Most of the estates we act in here involve a long-held canal or beachside home, a modest share portfolio and superannuation — and the will was often signed decades ago.",
  },
  {
    t: "How the title is held decides whether probate is needed",
    d: "Joint tenants pass to the survivor by survivorship, so a grant is often unnecessary. Sole ownership or tenants in common does not, and a grant of probate is generally required before the property can be transmitted and sold.",
  },
  {
    t: "Superannuation is not covered by the will",
    d: "Super is paid under the fund's rules and any binding death benefit nomination, not automatically under the will. For Golden Beach couples with a self-managed fund or an out-of-date nomination, that is the most common gap we find.",
  },
  {
    t: "Downsizing, retirement villages and aged care",
    d: "Moving from the family home into a village or aged care changes capacity, contract and estate questions at the same time. We handle the sale, the contract review and the will update together rather than in three separate files.",
  },
  {
    t: "Capacity and enduring powers of attorney",
    d: "A will or enduring power of attorney can only be made while the person still has capacity. Where capacity is doubtful we take instructions carefully, record the assessment and, where appropriate, obtain a medical opinion before the document is signed.",
  },
  {
    t: "Time limits on estate claims",
    d: "A family provision claim in Queensland requires notice within six months of death and the claim filed within nine months. Executors should not distribute before those dates pass without advice.",
  },
];

const FAQS = [
  {
    q: "Who prepares wills in Golden Beach?",
    a: `${FIRM.legalName}, a Queensland law firm at ${FIRM_ADDRESS}. Wills, enduring powers of attorney and advance health directives are prepared by an admitted solicitor, and we see clients at the Theresa Street office or visit at home, in hospital or in aged care when needed.`,
  },
  {
    q: "How much does a will cost in Golden Beach?",
    a: "A simple will is from $660, a will and enduring power of attorney pack is $825, and a couples pack of mirror wills and enduring powers of attorney is $1,499 — all inclusive of GST. Wills involving blended families, companies, trusts or SMSFs are quoted separately before any work starts.",
  },
  {
    q: "Do I need probate for a Golden Beach house?",
    a: "It depends on how the title is held and what the bank or Titles Queensland requires. If the property was held as joint tenants it passes to the surviving owner by survivorship and a grant is often unnecessary. If it was held solely or as tenants in common, a grant of probate is generally required before the property can be transmitted and sold.",
  },
  {
    q: "How long does probate take in Queensland?",
    a: "Roughly four to eight weeks from advertising the intention to apply to the grant issuing, assuming the will is straightforward and the Supreme Court registry is not requisitioning. Administering the estate afterwards — collecting assets, selling property, distributing — usually takes six to twelve months.",
  },
  {
    q: "Can you handle the will and the conveyancing for the same family?",
    a: "Yes. Many Golden Beach clients update the will when they downsize or buy, and we act for executors selling an estate property. The conveyancing side is set out on our Golden Beach conveyancing page.",
  },
  {
    q: "Do you visit clients at home in Golden Beach?",
    a: "Yes. For wills, enduring powers of attorney and advance health directives we visit at home, in hospital or in aged care across Golden Beach, Pelican Waters, Caloundra, Kings Beach and Currimundi at no extra charge.",
  },
];

const erin = PEOPLE.find((p) => p.slug === "erin-wilkinson")!;
const michael = PEOPLE.find((p) => p.slug === "michael-klein")!;
const erinImage = `${SITE}${erin.photo}`;
const michaelImage = `${SITE}${michael.photo}`;

/**
 * Golden Beach is the firm's real premises, so this LegalService node carries
 * the office address directly and is tied to the site Organization.
 */
const legalServiceSchema = {
  "@context": "https://schema.org",
  "@type": "LegalService",
  "@id": `${CANONICAL}#service`,
  name: `${FIRM.name} — wills, estates and probate solicitors, Golden Beach`,
  description: DESCRIPTION,
  url: CANONICAL,
  telephone: FIRM.phone,
  email: FIRM.email,
  priceRange: "$$",
  currenciesAccepted: "AUD",
  parentOrganization: { "@id": `${SITE}/#organization` },
  address: {
    "@type": "PostalAddress",
    streetAddress: FIRM.street,
    addressLocality: FIRM.suburb,
    addressRegion: FIRM.state,
    postalCode: FIRM.postcode,
    addressCountry: FIRM.country,
  },
  areaServed: [
    { "@type": "Place", name: "Golden Beach QLD 4551" },
    { "@type": "Place", name: "Pelican Waters QLD 4551" },
    { "@type": "Place", name: "Caloundra QLD 4551" },
    { "@type": "Place", name: "Kings Beach QLD 4551" },
    { "@type": "Place", name: "Little Mountain QLD 4551" },
  ],
  knowsAbout: [
    "Wills",
    "Enduring powers of attorney",
    "Advance health directives",
    "Probate",
    "Letters of administration",
    "Estate administration",
    "Family provision claims",
    "Granny flat agreements",
  ],
  employee: [
    {
      "@type": "Person",
      name: erin.name,
      jobTitle: erin.role,
      image: {
        "@type": "ImageObject",
        url: erinImage,
        caption: `${erin.name}, ${erin.role} at ${FIRM.name}, wills and estates at Golden Beach`,
      },
      knowsAbout: [...erin.areas],
    },
    {
      "@type": "Person",
      name: michael.name,
      jobTitle: michael.role,
      image: {
        "@type": "ImageObject",
        url: michaelImage,
        caption: `${michael.name}, ${michael.role} at ${FIRM.name}, probate and estates`,
      },
      knowsAbout: [...michael.areas],
    },
  ],
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Wills, estates and probate — Golden Beach",
    itemListElement: SERVICES.map((s) => ({
      "@type": "Offer",
      name: s.name,
      description: s.body,
      priceCurrency: "AUD",
      url: `${SITE}${s.to}`,
    })),
  },
  isRelatedTo: {
    "@type": "WebPage",
    name: `${FIRM.name} — conveyancing solicitors, Golden Beach`,
    url: CONVEYANCING,
  },
};

const webPageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": `${CANONICAL}#webpage`,
  url: CANONICAL,
  name: TITLE,
  description: DESCRIPTION,
  inLanguage: "en-AU",
  isPartOf: { "@id": `${SITE}/#organization` },
  mainEntity: { "@id": `${CANONICAL}#service` },
  significantLink: [CONVEYANCING, `${SITE}/golden-beach`, `${SITE}/wills-and-probate-queensland`],
  about: { "@type": "Place", name: "Golden Beach, Queensland, Australia" },
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
    {
      "@type": "ListItem",
      position: 2,
      name: "Wills and probate in Queensland",
      item: `${SITE}/wills-and-probate-queensland`,
    },
    { "@type": "ListItem", position: 3, name: "Golden Beach", item: CANONICAL },
  ],
};

export const Route = createFileRoute("/wills-and-probate-golden-beach")({
  head: () => ({
    meta: [
      { title: fitTitle(TITLE) },
      { name: "description", content: fitDescription(DESCRIPTION) },
      { property: "og:title", content: fitTitle(TITLE) },
      { property: "og:description", content: fitDescription(DESCRIPTION) },
      { property: "og:url", content: CANONICAL },
      { property: "og:type", content: "website" },
      { property: "og:image", content: erinImage },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: erinImage },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(legalServiceSchema) },
      { type: "application/ld+json", children: JSON.stringify(webPageSchema) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbSchema) },
      { type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) },
    ],
  }),
  component: GoldenBeachWillsPage,
});

function GoldenBeachWillsPage() {
  return (
    <>
      <PageHero
        eyebrow="Golden Beach QLD 4551"
        title="Wills, estates and probate at Golden Beach"
        lead={`${FIRM.legalName} works from ${FIRM_ADDRESS}. We prepare wills and enduring powers of attorney at fixed fees and act for executors through probate — in the office, or at home, in hospital and in aged care.`}
      />

      <section className="mx-auto max-w-5xl px-5 pt-12">
        <div className="rounded-sm border border-border bg-secondary/40 p-6">
          <h2 className="text-xs font-semibold uppercase tracking-widest text-foreground">
            Wills and probate in Golden Beach — the short answer
          </h2>
          <p className="mt-3 text-base leading-relaxed text-muted-foreground">{ANSWER}</p>
          <p className="mt-3 text-xs text-muted-foreground">
            Also searched as: {VARIANTS.join(", ")}.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-5 py-14">
        <h2 className="text-xs font-semibold uppercase tracking-widest text-foreground">
          The details
        </h2>
        <dl className="mt-6 divide-y divide-border border-y border-border text-sm">
          {FACTS.map((f) => (
            <div key={f.label} className="grid gap-1 py-4 sm:grid-cols-[240px_1fr]">
              <dt className="font-semibold text-foreground">{f.label}</dt>
              <dd className="text-muted-foreground">{f.value}</dd>
            </div>
          ))}
        </dl>

        <div className="mt-6 flex flex-wrap gap-3">
          {CONTACT_NUMBERS.map((c) => (
            <a
              key={c.phone}
              href={c.phoneHref}
              className="rounded-sm border border-border bg-card px-4 py-3 text-sm font-semibold text-primary underline"
            >
              Call {c.name} — {c.phone}
            </a>
          ))}
        </div>
      </section>

      <section className="border-y border-border bg-secondary/40">
        <div className="mx-auto max-w-5xl px-5 py-14">
          <h2 className="font-serif text-3xl text-foreground">Services and fixed fees</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {SERVICES.map((s) => (
              <Link
                key={s.name}
                to={s.to}
                className="block rounded-sm border border-border bg-card p-6 transition hover:border-accent"
              >
                <p className="font-serif text-xl text-foreground">{s.name}</p>
                <p className="mt-1 text-sm font-semibold text-primary">{s.price}</p>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{s.body}</p>
              </Link>
            ))}
          </div>
          <p className="mt-5 text-xs leading-relaxed text-muted-foreground">
            Wills requiring complex estate planning — blended families, companies, trusts and SMSFs
            — are quoted separately before any work starts.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-5 py-14">
        <h2 className="font-serif text-3xl text-foreground">What comes up in Golden Beach estates</h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          {LOCAL_NOTES.map((n) => (
            <div key={n.t} className="rounded-sm border border-border bg-card p-6">
              <p className="font-serif text-xl text-foreground">{n.t}</p>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{n.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="border-y border-border bg-secondary/40">
        <div className="mx-auto max-w-5xl px-5 py-14">
          <h2 className="font-serif text-3xl text-foreground">Buying, selling or settling an estate</h2>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-muted-foreground">
            Most estates here involve the house. We act on both sides of that — the grant and the
            sale — so nothing waits on a second firm.
          </p>
          <ul className="mt-6 grid gap-2 sm:grid-cols-2">
            <li>
              <Link
                to="/conveyancing/$suburb"
                params={{ suburb: "golden-beach" }}
                className="block rounded-sm border border-border bg-card px-4 py-3 text-sm text-primary underline"
              >
                Conveyancing — Golden Beach
              </Link>
            </li>
            <li>
              <Link
                to="/golden-beach"
                className="block rounded-sm border border-border bg-card px-4 py-3 text-sm text-primary underline"
              >
                Golden Beach — all services
              </Link>
            </li>
            <li>
              <Link
                to="/probate-queensland"
                className="block rounded-sm border border-border bg-card px-4 py-3 text-sm text-primary underline"
              >
                Probate in Queensland
              </Link>
            </li>
            <li>
              <Link
                to="/executor-queensland"
                className="block rounded-sm border border-border bg-card px-4 py-3 text-sm text-primary underline"
              >
                Executor's guide
              </Link>
            </li>
            <li>
              <Link
                to="/wills-and-probate-queensland"
                className="block rounded-sm border border-border bg-card px-4 py-3 text-sm text-primary underline"
              >
                Wills and probate across Queensland
              </Link>
            </li>
            <li>
              <Link
                to="/elder-law"
                className="block rounded-sm border border-border bg-card px-4 py-3 text-sm text-primary underline"
              >
                Elder law and granny flat agreements
              </Link>
            </li>
          </ul>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-5 py-14">
        <Prose>
          <h2>Who does the work</h2>
          <p>
            Erin Wilkinson, admitted in 2025 and living at Golden Beach, takes most local will and
            estate appointments. Michael Klein, Legal Practice Director, admitted in 2003 and
            formerly managing partner of GKS Law, handles the contested and complex estates. Neither
            the will nor the probate file is passed to an unqualified processing team, and the fee is
            fixed before the work starts.
          </p>
          <p>
            Original wills are stored in safe custody at no charge, and we hold a record of where the
            executor can find them. If you already have a will with another firm, we can transfer it
            across.
          </p>
        </Prose>
        <div className="mt-8">
          <ReviewStrip />
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-5 pb-14">
        <h2 className="font-serif text-3xl text-foreground">Common questions</h2>
        <div className="mt-6">
          <FaqList items={FAQS} />
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-5 pb-20">
        <EnquiryForm
          heading="Talk to us about a will or an estate in Golden Beach"
          suburb="Golden Beach"
        />
      </section>
    </>
  );
}
